import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { CalendarX2, ListChecks, Loader2, Trash2, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import type { LeaveBlockedDate } from '@pms/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { DataTable, type Column } from '@/components/ui/data-table';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { leaveService } from '@/services/leave.service';
import { getErrorMessage } from '@/lib/errors';
import { formatDate, getTodayIST } from '@/lib/dateFormat';
import { cn } from '@/lib/utils';

const BLOCKED_KEY = ['leave', 'blocked-dates'] as const;
const REASON_MAX = 200;
const MAX_DATES_PER_SAVE = 366;
const SUNDAY = 0;

type View = 'upcoming' | 'past';

/**
 * HRMS Config > Leave Config > Blocked Dates. HR picks dates on which no leave may be
 * applied; /leave greys them out in both the Apply Leave and Short Leave calendars and the
 * server rejects them. Leave already applied on a date is left as it is.
 */
export function LeaveBlockedDatesSection() {
  const queryClient = useQueryClient();
  const todayStr = getTodayIST();
  const today = useMemo(() => parseISO(todayStr), [todayStr]);

  const [selected, setSelected] = useState<Date[]>([]);
  const [reason, setReason] = useState('');
  const [view, setView] = useState<View>('upcoming');
  const [pendingDelete, setPendingDelete] = useState<LeaveBlockedDate | null>(null);

  // One row per day, so the whole list stays small; it feeds both the calendar and the table
  const listQuery = useQuery({
    queryKey: [...BLOCKED_KEY, 'all'],
    queryFn: () => leaveService.listBlockedDates(),
  });
  const all = useMemo(() => listQuery.data ?? [], [listQuery.data]);

  const blockedDays = useMemo(() => all.map((b) => parseISO(b.blocked_date)), [all]);
  const rows = useMemo(
    () =>
      view === 'upcoming'
        ? all.filter((b) => b.blocked_date >= todayStr)
        : all.filter((b) => b.blocked_date < todayStr).reverse(),
    [all, view, todayStr],
  );
  const upcomingCount = all.filter((b) => b.blocked_date >= todayStr).length;

  const sortedSelected = useMemo(
    () => [...selected].sort((a, b) => a.getTime() - b.getTime()),
    [selected],
  );

  const addMutation = useMutation({
    mutationFn: () =>
      leaveService.addBlockedDates({
        dates: sortedSelected.map((d) => format(d, 'yyyy-MM-dd')),
        reason: reason.trim() || undefined,
      }),
    onSuccess: (saved) => {
      const alreadyApplied = saved.reduce((sum, b) => sum + b.existing_requests, 0);
      toast.success(`Blocked ${saved.length} date${saved.length === 1 ? '' : 's'} for leave`, {
        description: alreadyApplied > 0
          ? `${alreadyApplied} leave request(s) already on these dates were kept as they are.`
          : undefined,
      });
      setSelected([]);
      setReason('');
      setView('upcoming');
      queryClient.invalidateQueries({ queryKey: BLOCKED_KEY });
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => leaveService.deleteBlockedDate(id),
    onSuccess: () => {
      toast.success('Date unblocked — leave can be applied on it again');
      queryClient.invalidateQueries({ queryKey: BLOCKED_KEY });
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const columns: Column<LeaveBlockedDate>[] = [
    {
      key: 'date',
      header: 'Date',
      cell: (b) => (
        <div>
          <p className="font-medium">{formatDate(b.blocked_date)}</p>
          <p className="text-xs text-muted-foreground">{format(parseISO(b.blocked_date), 'EEEE')}</p>
        </div>
      ),
    },
    {
      key: 'reason',
      header: 'Reason',
      cell: (b) =>
        b.reason ? <span>{b.reason}</span> : <span className="text-xs text-muted-foreground">—</span>,
    },
    {
      key: 'existing',
      header: 'Already applied',
      cell: (b) =>
        b.existing_requests > 0 ? (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
            title="Pending or approved leave on this date — not changed by the block"
          >
            <AlertTriangle className="h-3 w-3" />
            {b.existing_requests} request{b.existing_requests === 1 ? '' : 's'}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">None</span>
        ),
    },
    {
      key: 'added_by',
      header: 'Added by',
      cell: (b) => <span className="text-sm">{b.created_by_name ?? '—'}</span>,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-16 text-right',
      cell: (b) => (
        <Button
          size="icon"
          variant="ghost"
          aria-label={`Unblock ${formatDate(b.blocked_date)}`}
          className="text-muted-foreground hover:text-destructive"
          disabled={deleteMutation.isPending}
          onClick={() => setPendingDelete(b)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <Card>
        <SectionHeader
          icon={<CalendarX2 />}
          title="Block dates for leave"
          hint="No leave or short leave can be applied on these dates"
        />
        <CardContent className="p-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-start">
            <div className="self-center rounded-lg border border-border/60 md:self-start">
              <Calendar
                mode="multiple"
                max={MAX_DATES_PER_SAVE}
                selected={selected}
                onSelect={(days) => setSelected(days ?? [])}
                disabled={[{ before: today }, { dayOfWeek: [SUNDAY] }]}
                startMonth={today}
                modifiers={{ blocked: blockedDays }}
                modifiersClassNames={{
                  blocked:
                    '[&>button]:bg-rose-100 [&>button]:text-rose-700 [&>button]:font-semibold ' +
                    'dark:[&>button]:bg-rose-900/40 dark:[&>button]:text-rose-300',
                }}
              />
              <div className="flex flex-wrap items-center gap-3 border-t border-border/60 px-3 py-2 text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <span className="h-3 w-3 rounded bg-rose-100 dark:bg-rose-900/40" /> Already blocked
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-3 w-3 rounded bg-primary" /> Selected
                </span>
                <span>Sundays are always off</span>
              </div>
            </div>

            <div className="min-w-0 flex-1 space-y-4">
              <div>
                <p className="mb-1.5 text-xs font-medium text-foreground">
                  Selected dates{' '}
                  <span className="text-muted-foreground">({sortedSelected.length})</span>
                </p>
                {sortedSelected.length === 0 ? (
                  <p className="rounded-lg border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
                    Click dates in the calendar to select them. Picking an already-blocked date
                    again replaces its reason.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {sortedSelected.map((d) => (
                      <span
                        key={d.getTime()}
                        className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 py-0.5 pl-2.5 pr-1 text-xs font-medium text-primary"
                      >
                        {format(d, 'dd MMM yyyy')}
                        <button
                          type="button"
                          aria-label={`Remove ${format(d, 'dd MMM yyyy')}`}
                          onClick={() => setSelected((prev) => prev.filter((p) => p.getTime() !== d.getTime()))}
                          className="rounded-full p-0.5 hover:bg-primary/20"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="blocked-reason" className="mb-1.5 block text-xs font-medium text-foreground">
                  Reason <span className="text-muted-foreground">(optional, shown to staff)</span>
                </label>
                <Input
                  id="blocked-reason"
                  value={reason}
                  maxLength={REASON_MAX}
                  placeholder="e.g. Annual audit, Diwali sale, Company event"
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>

              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                {sortedSelected.length > 0 && (
                  <Button variant="outline" onClick={() => setSelected([])} disabled={addMutation.isPending}>
                    Clear
                  </Button>
                )}
                <Button
                  onClick={() => addMutation.mutate()}
                  disabled={sortedSelected.length === 0 || addMutation.isPending}
                >
                  {addMutation.isPending
                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    : <CalendarX2 className="mr-2 h-4 w-4" />}
                  Block {sortedSelected.length || ''} date{sortedSelected.length === 1 ? '' : 's'}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <SectionHeader
          icon={<ListChecks />}
          title="Blocked dates"
          action={
            <div className="inline-flex rounded-lg border border-border/60 p-0.5">
              {(['upcoming', 'past'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setView(v)}
                  className={cn(
                    'rounded-md px-3 py-1 text-xs font-medium capitalize transition-colors',
                    view === v ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted/50',
                  )}
                >
                  {v}{v === 'upcoming' && upcomingCount > 0 ? ` (${upcomingCount})` : ''}
                </button>
              ))}
            </div>
          }
        />
        <CardContent className="p-4">
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(b) => b.id}
            loading={listQuery.isLoading}
            loadingRows={4}
            error={listQuery.isError}
            onRetry={() => void listQuery.refetch()}
            empty={
              view === 'upcoming'
                ? 'No upcoming blocked dates. Select dates in the calendar above to block them.'
                : 'No past blocked dates.'
            }
          />
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => { if (!o) setPendingDelete(null); }}
        title={`Unblock ${pendingDelete ? formatDate(pendingDelete.blocked_date) : ''}?`}
        description="Staff will be able to apply leave and short leave on this date again."
        confirmLabel="Unblock"
        destructive
        onConfirm={() => { if (pendingDelete) deleteMutation.mutate(pendingDelete.id); }}
      />
    </div>
  );
}
